/**
 * Persistence for the user's telemetry-consent decision.
 *
 * Storing the decision itself is "strictly necessary" (it records a choice
 * the user made), so it is exempt from consent — remembering "no" is allowed.
 * We keep it in localStorage to mirror the other first-party preferences
 * (see language-storage.ts) rather than a cookie, since Forma sets no cookies.
 */

export type ConsentDecision = 'granted' | 'denied';

export const consentStorageKey = 'forma:telemetry-consent';

type ConsentRecord = {
  decision: ConsentDecision;
  // ISO timestamp of the decision, useful for expiring stale consent later.
  decidedAt: string;
  // Version lets you re-prompt when the telemetry scope materially changes.
  version: number;
};

export const CONSENT_VERSION = 1;

function getStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readConsentDecision(
  storage: Storage | null = getStorage(),
): ConsentDecision | null {
  if (!storage) {
    return null;
  }
  try {
    const raw = storage.getItem(consentStorageKey);
    if (!raw) {
      return null;
    }
    const record = JSON.parse(raw) as Partial<ConsentRecord>;
    if (record.version !== CONSENT_VERSION) {
      return null; // Scope changed since they decided → re-prompt.
    }
    return record.decision === 'granted' || record.decision === 'denied'
      ? record.decision
      : null;
  } catch {
    return null;
  }
}

export function writeConsentDecision(
  decision: ConsentDecision,
  decidedAt: string,
  storage: Storage | null = getStorage(),
): void {
  if (!storage) {
    return;
  }
  try {
    const record: ConsentRecord = {
      decision,
      decidedAt,
      version: CONSENT_VERSION,
    };
    storage.setItem(consentStorageKey, JSON.stringify(record));
  } catch {
    // A blocked/full store must not break the app; consent just won't persist.
  }
}
